import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ValidateAddressResponse } from "types/addressValidation";
import type { ValidatableAddress } from "./useAddressValidationFlow";

type AddressValidationDialogProps = {
  open: boolean;
  address: ValidatableAddress;
  formattedAddress: ValidatableAddress;
  validationResponse: ValidateAddressResponse;
  onClose: () => void;
  onProceed: () => void;
  onFix: () => void;
  onApplyFormattedAddress: () => void;
};

export default function AddressValidationDialog({
  open,
  address,
  formattedAddress,
  validationResponse,
  onClose,
  onProceed,
  onFix,
  onApplyFormattedAddress,
}: AddressValidationDialogProps) {
  const nextAction = validationResponse.result.verdict.possibleNextAction;
  const [option, setOption] = useState("");

  useEffect(() => {
    if (open) setOption("");
  }, [open, nextAction]);

  const handleContinue = () => {
    switch (option) {
      case "fix":
        onFix();
        break;
      case "replace":
        onApplyFormattedAddress();
        onProceed();
        break;
      case "keep":
        onProceed();
        break;
      default:
        break;
    }
  };

  if (nextAction !== "CONFIRM" && nextAction !== "CONFIRM_ADD_SUBPREMISES" && nextAction !== "ACCEPT") {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent>
        {(nextAction === "CONFIRM" || nextAction === "ACCEPT") && (
          <ConfirmAddressFormat
            address={address}
            formattedAddress={formattedAddress}
            option={option}
            onOptionChange={setOption}
          />
        )}
        {nextAction === "CONFIRM_ADD_SUBPREMISES" && (
          <ConfirmAddSubpremises address={address} option={option} onOptionChange={setOption} />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" disabled={!option} onClick={handleContinue}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type ConfirmOptionProps = {
  option: string;
  onOptionChange: (option: string) => void;
};

function AddressLines({ address }: { address: ValidatableAddress }) {
  const cityState = [address.city, address.state].filter(Boolean).join(", ");
  const cityStateZipcode = [cityState, address.zipcode].filter(Boolean).join(" ");

  return (
    <>
      <Typography>{address.address}</Typography>
      {address.address2 && <Typography>{address.address2}</Typography>}
      {cityStateZipcode && <Typography>{cityStateZipcode}</Typography>}
      {address.country && <Typography>{address.country}</Typography>}
    </>
  );
}

function ConfirmAddressFormat({
  address,
  formattedAddress,
  option,
  onOptionChange,
}: {
  address: ValidatableAddress;
  formattedAddress: ValidatableAddress;
} & ConfirmOptionProps) {
  return (
    <FormControl component={Stack} gap={2}>
      <Typography>Please confirm the address format you want to use:</Typography>
      <RadioGroup sx={{ gap: 2 }} value={option} onChange={(e) => onOptionChange(e.target.value)}>
        <FormControlLabel
          value="replace"
          control={<Radio />}
          label={
            <Box sx={{ margin: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>Formatted Address</Typography>
              <AddressLines address={formattedAddress} />
            </Box>
          }
        />
        <FormControlLabel
          value="keep"
          control={<Radio />}
          label={
            <Box sx={{ margin: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>Entered Address</Typography>
              <AddressLines address={address} />
            </Box>
          }
        />
      </RadioGroup>
    </FormControl>
  );
}

function ConfirmAddSubpremises({
  address,
  option,
  onOptionChange,
}: { address: ValidatableAddress } & ConfirmOptionProps) {
  return (
    <FormControl component={Stack} gap={2}>
      <Typography>The address you entered may be missing a unit number. Please review the address below:</Typography>
      <Box component={Paper} variant="outlined" sx={{ padding: 2 }}>
        <Typography sx={{ fontWeight: 600 }}>Entered Address</Typography>
        <AddressLines address={address} />
      </Box>
      <RadioGroup value={option} onChange={(e) => onOptionChange(e.target.value)}>
        <FormControlLabel value="fix" control={<Radio />} label="Go back and add a unit number" />
        <FormControlLabel value="keep" control={<Radio />} label="Continue without unit number" />
      </RadioGroup>
    </FormControl>
  );
}
